import { marked } from "marked";
import DOMPurify from "dompurify";
import {
  EXTERNAL_LINK_ATTRS,
  KB_ALL_EXTRA_HTML_ATTRIBUTES,
  KB_ALL_EXTRA_TAGS,
  KB_SRC_PROTOCOLS,
  isExternalHref,
  resolveMediaUrl,
  stripLeadingH1,
  transformWikilinks,
} from "@skycell-ag/kb-core";

export { readingMinutes } from "@skycell-ag/kb-core";

// Render KB article markdown to sanitized HTML for the widget.
//
// The main reader uses a react-markdown + rehype pipeline
// (src/lib/sanitize.ts + MarkdownView). This self-contained widget can't pull
// React in, so it uses marked + DOMPurify — lean and safe. GitHub-style alert
// callouts and heading-anchor parity are a follow-up (would mean reusing the
// react-markdown pipeline via a preact/compat build).
//
// The source transforms and the HTML allow-list are kb-core's, shared with the
// reader (VSI-506/VSI-507). Only the *enforcement* differs: DOMPurify here,
// rehype-sanitize there. kb-core's root entry is dependency-free, so importing
// it does not pull rehype into this IIFE bundle.

marked.setOptions({ gfm: true, breaks: false });

// Harden links. External (http[s]) links open in a new tab so the widget never
// navigates the partner page away. Internal /kb/<slug> links are left relative
// (no target) so App can intercept them and open the article in-widget.
// DOMPurify strips scripts/handlers regardless. Media and embeds must carry an
// http(s) src — the same protocol restriction KB_SANITIZE_SCHEMA pins for the
// reader, enforced here by hand because DOMPurify has no per-attribute
// protocol allow-list.
const SRC_PROTOCOL_RE = new RegExp(`^(?:${KB_SRC_PROTOCOLS.join("|")}):\\/\\/`, "i");

// Root-relative media (`/kb-images/…`) is resolved against the KB origin after
// sanitizing, so it is exempt from the protocol check that embeds must pass.
const EMBED_TAGS = new Set(["IFRAME"]);

DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (EMBED_TAGS.has(node.tagName)) {
    const src = node.getAttribute("src") ?? "";
    if (!SRC_PROTOCOL_RE.test(src)) node.remove();
    return;
  }
  if (node.tagName !== "A") return;
  if (isExternalHref(node.getAttribute("href") ?? "")) {
    for (const [attr, value] of Object.entries(EXTERNAL_LINK_ATTRS)) {
      node.setAttribute(attr, value);
    }
  }
});

// Resolve root-relative media URLs against the KB origin — article images and
// videos live in the Supabase Storage bucket, not on the partner host. Covers
// <img src>, <video src>/<video poster> and <source src> so embedded video
// resolves too. Two attribute-scoped passes, because a single regex can't match
// two overlapping attrs on the same tag (<video src="…" poster="…">).
function absolutizeMediaUrls(html: string, kbUrl: string): string {
  const rewrite = (h: string, attr: RegExp) =>
    h.replace(
      attr,
      (_m, pre: string, path: string, post: string) =>
        `${pre}${resolveMediaUrl(path, kbUrl)}${post}`,
    );
  let out = rewrite(html, /(<(?:img|video|source)\b[^>]*\bsrc=")(\/[^"]*)(")/gi);
  out = rewrite(out, /(<video\b[^>]*\bposter=")(\/[^"]*)(")/gi);
  return out;
}

export function renderMarkdown(md: string, kbUrl: string): string {
  // No base path: the widget runs on the partner's origin, so internal links
  // must stay `/kb/<slug>` for App.onContentClick to intercept them.
  const source = transformWikilinks(stripLeadingH1(md ?? ""));
  const raw = marked.parse(source, { async: false }) as string;
  const clean = DOMPurify.sanitize(raw, {
    ADD_TAGS: [...KB_ALL_EXTRA_TAGS],
    ADD_ATTR: ["target", "rel", ...KB_ALL_EXTRA_HTML_ATTRIBUTES],
  });
  return absolutizeMediaUrls(clean, kbUrl);
}
